import type JCE from '@chesscom/js-chess-engine';
import { MoveList } from 'chessboard/model/move-list/types';
import { getTimestampFromNode } from 'chessboard/model/move/get-timestamp-from-node';
import { TIMESTAMP_SINGLE_DIGIT_WIDTH } from 'components/move-list/config/types';

/**
 * Transform number of deciseconds into a days/hours object.
 *
 * @example
 * decisecondsToDaysHoursMinutesObject(25410600);
 * // { days: 2, hours: 22, minutes: 35 }
 */
export function decisecondsToDaysHoursMinutesObject(deciseconds: number) {
  const roundedSeconds = Math.ceil(deciseconds / 10);
  let days = Math.floor(roundedSeconds / (3600 * 24));
  let hours = Math.floor((roundedSeconds % (3600 * 24)) / 3600);
  let minutes = Math.ceil((roundedSeconds % 3600) / 60);

  if (minutes === 60) {
    minutes = 0;
    hours += 1;
  }

  if (hours === 24) {
    hours = 0;
    days += 1;
  }

  return {
    days,
    hours,
    minutes,
  };
}

/**
 * Transform number of deciseconds into a days and hours string.
 *
 * @example
 * decisecondsToDaysHoursMinutesString(25410600);
 * // '2 days, 22 hours, 35 minutes'
 */
export function decisecondsToDaysHoursMinutesString(
  deciseconds: number,
  translations?: MoveList.Translations,
): string {
  const { days, hours, minutes } = decisecondsToDaysHoursMinutesObject(deciseconds);

  const padding = {
    days: hours > 0 || minutes > 0 ? ', ' : '',
    hours: minutes > 0 ? ', ' : '',
  };

  const daysString =
    days > 0
      ? `${translations?.[MoveList.Translation.Plural.NumberOfDays](days)}${padding.days}`
      : '';

  const hoursString =
    hours > 0
      ? `${translations?.[MoveList.Translation.Plural.NumberOfHours](hours)}${padding.hours}`
      : '';

  const minutesString =
    minutes > 0 ? `${translations?.[MoveList.Translation.Plural.NumberOfMinutes](minutes)}` : '';

  return `${daysString}${hoursString}${minutesString}`;
}

/**
 * Convert deciseconds to a human readable format.
 *
 * @example
 * decisecondsToHumanReadableFormat(25410600); // 2 days
 * decisecondsToHumanReadableFormat(110000); // 3 hours
 * decisecondsToHumanReadableFormat(25000); // 4 mins
 */
export function decisecondsToHumanReadableFormat(
  deciseconds: number,
  translations: MoveList.Translations,
): string {
  const { days, hours, minutes } = decisecondsToDaysHoursMinutesObject(deciseconds);

  if (days) {
    return days > 0 ? `${translations[MoveList.Translation.Plural.NumberOfDays](days)}` : '';
  }

  if (hours) {
    return hours > 0 ? `${translations[MoveList.Translation.Plural.NumberOfHours](hours)}` : '';
  }

  return minutes > 0 ? `${translations[MoveList.Translation.Plural.NumberOfMinutes](minutes)}` : '';
}

/**
 * Format deciseconds as clock string.
 */
export function formatDecisecondsAsClockString(deciseconds: number) {
  const minutes = Math.floor(deciseconds / 600);
  const seconds = ((deciseconds - minutes * 600) / 10).toFixed(1);

  if (minutes === 0) {
    return seconds;
  }

  const paddedSeconds = `0${seconds}`.substr(-4);

  return `${minutes}:${paddedSeconds}`;
}

/**
 * Get largest timestamp in a line.
 */
export function getLargestAndWidestTimestamps(
  line: JCE.Line,
  {
    displayTimeInClockFormat,
    translations,
  }: {
    displayTimeInClockFormat: boolean;
    translations: MoveList.Translations;
  },
):
  | {
      largestTimestamp: number;
      largestWidth: number;
    }
  | undefined {
  let largestTimestamp: number | undefined;
  let largestWidth: number | undefined;

  [...line].forEach((node) => {
    if (!node) {
      return;
    }

    const value = getTimestampFromNode(node);

    if (value === undefined || value === null) {
      return;
    }

    const { timestamp, width } = getTimestampData({
      displayTimeInClockFormat,
      timestamp: value,
      translations,
    });

    if (timestamp >= (largestTimestamp ?? 0)) {
      largestTimestamp = timestamp;
    }

    if (width >= (largestWidth ?? 0)) {
      largestWidth = width;
    }
  });

  if (largestTimestamp === undefined || largestWidth === undefined) {
    return;
  }

  return {
    largestTimestamp,
    largestWidth,
  };
}

/**
 * Get the parameters for a timestamp element.
 *
 * @param displayTimeInClockFormat - Whether to display the time in "clock" format (i.e. true: 1:24.1, false: 1 min).
 */
export function getTimestampData({
  displayTimeInClockFormat,
  timestamp,
  translations,
}: {
  displayTimeInClockFormat: boolean;
  timestamp: number;
  translations: MoveList.Translations;
}) {
  const timestampParameters = {
    title: displayTimeInClockFormat
      ? undefined
      : decisecondsToDaysHoursMinutesString(timestamp, translations),

    value: displayTimeInClockFormat
      ? formatDecisecondsAsClockString(timestamp)
      : decisecondsToHumanReadableFormat(timestamp, translations),
  };

  return {
    timestamp,
    width: timestampParameters.value.length * TIMESTAMP_SINGLE_DIGIT_WIDTH,
    value: timestampParameters.value,
  };
}
